import {
  SystemStyleObject,
  createMultiStyleConfigHelpers,
  defineStyle,
} from '@chakra-ui/react';
import { tabsAnatomy } from '@chakra-ui/anatomy';
import { TabSizes } from './tabs';
import {
  getBorderWidthToken,
  getTypographyToken,
  ColorMode,
} from '@allergan-data-labs/alle-elements-core';

const { defineMultiStyleConfig, definePartsStyle } =
  createMultiStyleConfigHelpers(tabsAnatomy.keys);

interface TabsStyleProps {
  orientation?: 'vertical' | 'horizontal';
  isFitted?: boolean;
  align?: 'end' | 'center' | 'start';
}

const isVerticalOrientation = ({ orientation }: TabsStyleProps) =>
  orientation === 'vertical';

const getAlignments = (align = 'start') =>
  ({
    end: 'flex-end',
    center: 'center',
    start: 'flex-start',
  }[align]);

const baseStyleRoot = defineStyle((props) => {
  const { colorScheme: c, showMoreMenu } = props;
  return {
    display: isVerticalOrientation(props)
      ? 'flex'
      : showMoreMenu
      ? 'block'
      : 'flex',
    borderColor: `${c}.Border/Neutral/Subtle 2`,
    bg: `${c}.Container/Neutral/Base`,
    pt: 6,
    pl: 6,
    overflow: showMoreMenu ? undefined : 'auto',
    flexDirection: isVerticalOrientation(props) ? 'row' : 'column',
  };
});

const baseStyleTab = defineStyle((props) => {
  const { colorScheme: c, isFitted } = props;
  return {
    cursor: 'pointer',
    flex: isFitted ? 1 : undefined,
    whiteSpace: 'nowrap',
    borderRadius: 'Small',
    mx: 4,
    _selected: {
      color: `${c}.Text/Neutral/Interactive/Active support`,
    },
    '&[aria-selected=true]': {
      borderColor: 'transparent',
    },
    '&[aria-selected=false]': {
      _hover: {
        color: `${c}.Text/Neutral/Interactive/Hover`,
      },
    },
    _focusVisible: {
      outlineWidth: getBorderWidthToken('Bold'),
      outlineStyle: 'solid',
      outlineColor: `${c}.Border/Brand/Focus ring/Default`,
      boxShadow: 'none',
    },
    _active: {
      outline: 'none',
      bg: 'none',
    },
  };
});

const baseStyleTabList = defineStyle((props) => {
  const { colorScheme: c, align } = props;
  return {
    gap: '24px',
    justifyContent: getAlignments(align),
    alignItems: getAlignments(align),
    px: isVerticalOrientation(props) ? '1rem' : undefined,
    flexDirection: isVerticalOrientation(props) ? 'column' : 'row',
    pb: isVerticalOrientation(props) ? undefined : '.65625rem',
    color: `${c}.Text/Neutral/Interactive/Enable support`,
    outlineColor: `${c}.Border/Brand/Focus ring/Default`,
  };
});

const baseStyleTabPanel = defineStyle((props) => {
  const { colorScheme: c } = props;
  return {
    ...getTypographyToken('Body/Medium/Regular'),
    p: isVerticalOrientation(props) ? '0rem' : '.25rem',
    color: `${c}.Text/Neutral/Interactive/Active support`,
  };
});

const baseStyleTabIndicator = defineStyle((props) => {
  const { colorScheme: c } = props;
  return {
    mt: isVerticalOrientation(props) ? '0rem' : '-0.09375rem',
    ml: isVerticalOrientation(props) ? '-0.0625rem' : '0rem',
    height: isVerticalOrientation(props) ? '24px' : '2px',
    width: isVerticalOrientation(props) ? '2px' : '100%',
    bg: `${c}.Container/Brand/Medium 3`,
    borderColor: `${c}.Border/Neutral/Subtle 2`,
  };
});

const sizes = {
  md: definePartsStyle((props) => ({
    tab: {
      ...getTypographyToken('Body/Medium/Regular'),
      marginLeft: isVerticalOrientation(props) ? '1rem' : undefined,
      height: '24px',
      fontWeight: 'Medium',
    },
  })),
  lg: definePartsStyle((props) => ({
    tab: {
      ...getTypographyToken('Headline/Extra small/Primary medium'),
      marginLeft: isVerticalOrientation(props) ? '1rem' : undefined,
      height: '32px',
      fontWeight: 'Semibold',
    },
    indicator: {
      marginTop: 8,
    },
  })),
};

const baseStyle = definePartsStyle((props) => ({
  root: baseStyleRoot(props),
  tab: baseStyleTab(props),
  tablist: baseStyleTabList(props),
  tabpanel: baseStyleTabPanel(props),
  indicator: baseStyleTabIndicator(props),
}));

export const TabsTheme = defineMultiStyleConfig({
  baseStyle,
  sizes,
  defaultProps: {
    size: 'md',
  },
});

export const getTabFlexStyles = (
  colorMode: ColorMode,
  orientation: string,
  showMoreMenu: boolean,
  size: TabSizes,
  isFitted: boolean
): SystemStyleObject => {
  const isLarge = size === 'lg';
  const typographyToken = isLarge
    ? getTypographyToken('Headline/Extra small/Primary medium')
    : getTypographyToken('Body/Medium/Regular');
  const height = isLarge ? '34px' : '24px';
  const fontWeight = isLarge ? 'Semibold' : 'Medium';

  return {
    ...typographyToken,
    boxSizing: 'border-box',
    height,
    fontWeight,
    display: orientation === 'vertical' || !showMoreMenu ? 'flex' : 'block',
    alignItems: 'center',
    gap: '8px',
    p: '0.125rem',
    _hover: {
      color: `${colorMode}.Text/Neutral/Interactive/Hover support`,
    },
    flex: isFitted ? 1 : undefined,
    justifyContent: isFitted ? 'center' : undefined,
    _focus: {
      outline: 'none',
    },
    _focusVisible: {
      outlineWidth: getBorderWidthToken('Bold'),
      outlineStyle: 'solid',
      outlineColor: `${colorMode}.Border/Brand/Focus ring/Default`,
      boxShadow: 'none',
      outlineOffset: 2,
    },
    _active: {
      outline: 'none',
    },
    _selected: {
      color: `${colorMode}.Text/Neutral/Interactive/Active support`,
    },
  };
};

export const getTabListBorderStyles = (
  colorMode: ColorMode,
  orientation: string,
  showMoreMenu: boolean
): SystemStyleObject => {
  const flexStyle = showMoreMenu ? {} : { display: 'flex', flex: 1 };
  const orientationStyle =
    orientation === 'vertical'
      ? {
          borderLeftWidth: '1px',
          borderLeftColor: `${colorMode}.Border/Neutral/Subtle 2`,
        }
      : {
          borderBottomWidth: '1px',
          borderBottomColor: `${colorMode}.Border/Neutral/Subtle 2`,
        };
  return {
    border: '1px solid transparent',
    position: 'relative',
    ...flexStyle,
    ...orientationStyle,
  };
};

export const getTabsContainerStyles = (
  showMoreMenu: boolean
): SystemStyleObject => {
  return {
    position: 'relative',
    width: '100%',
    display: showMoreMenu ? 'block' : 'flex',
  };
};
